/**
 * Custom Hook for Audit Logging
 * Simplifies audit logging in React components
 */

import { useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { auditService } from '../services/auditService';
import { logger } from '../services/loggerService';
import { AuditAction, AuditResource } from '../types/audit';

export const useAuditLog = () => {
  const { currentUser, userProfile } = useAuth();

  const getUserInfo = useCallback(() => {
    return {
      id: currentUser?.uid || 'anonymous',
      email: currentUser?.email || 'unknown@email.com',
      name: userProfile?.name || currentUser?.displayName || 'Unknown User',
      role: userProfile?.role || 'UNKNOWN',
    };
  }, [currentUser, userProfile]);

  const logProductAction = useCallback(async (
    action: AuditAction,
    productId: string,
    productName: string,
    changes?: { before?: any; after?: any },
    success: boolean = true,
    errorMessage?: string
  ) => {
    try {
      const user = getUserInfo();
      await auditService.logProductAction({
        action,
        productId,
        productName,
        user,
        changes,
        success,
        errorMessage,
      });
      
      logger.info(`Product action logged: ${action}`, { productId, productName });
    } catch (error) {
      logger.error('Failed to log product action', error);
    }
  }, [getUserInfo]);

  const logCategoryAction = useCallback(async (
    action: AuditAction,
    categoryId: string,
    categoryName: string,
    changes?: { before?: any; after?: any },
    success: boolean = true,
    errorMessage?: string
  ) => {
    try {
      const user = getUserInfo();
      await auditService.logCategoryAction({
        action,
        categoryId,
        categoryName,
        user,
        changes,
        success,
        errorMessage,
      });
      
      logger.info(`Category action logged: ${action}`, { categoryId, categoryName });
    } catch (error) {
      logger.error('Failed to log category action', error);
    }
  }, [getUserInfo]);

  const logOrderAction = useCallback(async (
    action: AuditAction,
    orderId: string,
    changes?: { before?: any; after?: any },
    success: boolean = true,
    errorMessage?: string,
    additionalData?: Record<string, any>
  ) => {
    try {
      const user = getUserInfo();
      await auditService.logOrderAction({
        action,
        orderId,
        user,
        changes,
        success,
        errorMessage,
        additionalData,
      });
      
      logger.info(`Order action logged: ${action}`, { orderId });
    } catch (error) {
      logger.error('Failed to log order action', error);
    }
  }, [getUserInfo]);

  const logDeliveryAgentAction = useCallback(async (
    action: AuditAction,
    agentId: string,
    agentName: string,
    changes?: { before?: any; after?: any },
    success: boolean = true,
    errorMessage?: string
  ) => {
    try {
      const user = getUserInfo();
      await auditService.logDeliveryAgentAction({
        action,
        agentId,
        agentName,
        user,
        changes,
        success,
        errorMessage,
      });
      
      logger.info(`Delivery agent action logged: ${action}`, { agentId, agentName });
    } catch (error) {
      logger.error('Failed to log delivery agent action', error);
    }
  }, [getUserInfo]);

  const logAuthAction = useCallback(async (
    action: AuditAction,
    success: boolean,
    errorMessage?: string
  ) => {
    try {
      const email = currentUser?.email || 'unknown@email.com';
      const name = userProfile?.name || currentUser?.displayName || 'Unknown User';
      const userId = currentUser?.uid;

      await auditService.logAuth({
        action,
        userEmail: email,
        userName: name,
        userId,
        success,
        errorMessage,
      });
      
      logger.info(`Auth action logged: ${action}`, { email, success });
    } catch (error) {
      logger.error('Failed to log auth action', error);
    }
  }, [currentUser, userProfile]);

  return {
    logProductAction,
    logCategoryAction,
    logOrderAction,
    logDeliveryAgentAction,
    logAuthAction,
  };
};
